/**
 * Lokal lagring av brukerens EGNE lydfiler for reisefilmen (eier-ønske: «hvis
 * brukeren legger inn lyder selv i appen burde de bli lagret og være tilgjengelig
 * for brukeren»). Lagres i IndexedDB på ENHETEN — filene lastes ALDRI opp
 * (personvern-løftet i musikk-arket beholdes). Blob + metadata i én record.
 */

const DB_NAME = 'fricamp-replay';
const STORE = 'userAudio';
const DB_VERSION = 1;

export interface SavedAudio {
  id: string;
  name: string;
  size: number;
  addedAt: number;
  blob: Blob;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB utilgjengelig')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function makeId(name: string, addedAt: number): string {
  return `${addedAt}-${name}`.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);
}

/** Lagrer en lydfil lokalt og returnerer metadata-recorden. */
export async function saveUserAudio(file: File): Promise<SavedAudio> {
  const db = await openDb();
  const addedAt = Date.now();
  const rec: SavedAudio = { id: makeId(file.name, addedAt), name: file.name, size: file.size, addedAt, blob: file };
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(rec);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
  return rec;
}

/** Alle lagrede lyder, nyeste først. Tom liste hvis lagring feiler/utilgjengelig. */
export async function listUserAudio(): Promise<SavedAudio[]> {
  let db: IDBDatabase;
  try { db = await openDb(); } catch { return []; }
  try {
    const all = await new Promise<SavedAudio[]>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve((req.result as SavedAudio[]) || []);
      req.onerror = () => reject(req.error);
    });
    return all.sort((a, b) => b.addedAt - a.addedAt);
  } catch {
    return [];
  } finally {
    db.close();
  }
}

/** Sletter en lagret lyd. */
export async function deleteUserAudio(id: string): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}
